import React, { createContext, useState, useEffect, ReactNode } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { auth, firestore } from "../firebase";
import { useAuthState } from 'react-firebase-hooks/auth';

import { Influencer } from "@foodfluence/shared";
import { getInfluencerByEmail, getInfluencerByPhoneNumber } from "services/firebaseService";
import { influencerApi } from "services/api/influencer";
import { isFoodfluenceEmail } from "../utils/authUtils";

const SWITCHED_INFLUENCER_ID_KEY = 'switchedInfluencerId';

interface InfluencerDataContextType {
    influencerData: Influencer | null;
    loading: boolean;
    switchToInfluencer: (influencer: Influencer) => void;
    resetToOwnAccount: () => void;
    isSwitchedAccount: boolean;
}

export const InfluencerDataContext = createContext<InfluencerDataContextType>({
    influencerData: null,
    loading: true,
    switchToInfluencer: () => {},
    resetToOwnAccount: () => {},
    isSwitchedAccount: false
});

interface InfluencerDataProviderProps {
    children: ReactNode;
}

export const InfluencerDataProvider: React.FC<InfluencerDataProviderProps> = ({ children }) => {
    const [user] = useAuthState(auth);
    const [influencerData, setInfluencerData] = useState<Influencer | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [switchedInfluencerId, setSwitchedInfluencerId] = useState<string | null>(null);
    const [isSwitchedAccount, setIsSwitchedAccount] = useState<boolean>(false);

    // Load switched influencer ID from localStorage on mount
    useEffect(() => {
        const storedId = localStorage.getItem(SWITCHED_INFLUENCER_ID_KEY);
        if (storedId) {
            setSwitchedInfluencerId(storedId);
            setIsSwitchedAccount(true);
        }
    }, []);

    useEffect(() => {
        if (!user) {
            setLoading(false);
            setInfluencerData(null);
            setIsSwitchedAccount(false);
            setSwitchedInfluencerId(null);
            localStorage.removeItem(SWITCHED_INFLUENCER_ID_KEY);
            return;
        }

        // Check if user has foodfluence email and switched account is enabled
        const canSwitchAccount = isFoodfluenceEmail(user.email);
        const shouldUseSwitchedAccount = canSwitchAccount && switchedInfluencerId;

        // The lookups and the claim below are awaited, so a sign-out or account switch can land
        // mid-flight. Without this the stale run still installed its listener and overwrote the
        // newer effect's state; and the listener was returned from this inner function, which
        // React never sees, so it leaked.
        let cancelled = false;
        let unsubscribe: (() => void) | null = null;

        const fetchInfluencerData = async () => {
            let influencerDoc;
            let influencerId: string | null = null;

            if (shouldUseSwitchedAccount) {
                // Use switched influencer ID
                influencerId = switchedInfluencerId;
            } else {
                // Use normal lookup by phone/email
                if (user.phoneNumber) {
                    influencerDoc = await getInfluencerByPhoneNumber(user.phoneNumber);
                } 
                if (user.email && !influencerDoc) {
                    influencerDoc = await getInfluencerByEmail(user.email);
                }
                influencerId = influencerDoc?.id || null;

                // Nothing matched, which is what an email change in flight looks like from here:
                // Firebase Auth already holds the new address while email_address still holds the
                // old, so the creator resolves by neither that nor a phone number. Claim reconciles
                // it server-side from the verified token claim — it is idempotent, and a no-op
                // returning committed: false for anyone who simply has no creator record.
                //
                // It lives here rather than on the profile page because this is where resolution
                // actually fails; the creator lands wherever the sign-in link sent them, which is
                // usually not /profile. Gating on a failed lookup also keeps the call off the happy
                // path, which matters: /email/claim is rate limited per IP, not per user — so
                // staff (who never have a creator record) and unverified addresses (which can
                // never have landed via a verify link) skip it too.
                if (!influencerId && user.email && user.emailVerified && !canSwitchAccount) {
                    try {
                        const { committed } = await influencerApi.claimEmailChange();
                        if (committed) {
                            influencerDoc = await getInfluencerByEmail(user.email);
                            influencerId = influencerDoc?.id || null;
                        }
                    } catch (error) {
                        console.error("Error claiming email change:", error);
                    }
                }
            }

            if (cancelled) return;

            if (influencerId) {
                unsubscribe = onSnapshot(doc(firestore, "influencers", influencerId), (docSnapshot) => {
                    if (docSnapshot.exists()) {
                        const influencerData = docSnapshot.data() as Influencer;
                        influencerData.id = influencerId!;
                        setInfluencerData(influencerData);
                    } else {
                        setInfluencerData(null);
                    }
                    setLoading(false);
                }, (error) => {
                    console.error("Error fetching influencer data:", error);
                    setLoading(false);
                });
            } else {
                setInfluencerData(null);
                setLoading(false);
            }
        };

        fetchInfluencerData();

        return () => {
            cancelled = true;
            unsubscribe?.();
        };
    }, [user, switchedInfluencerId]);

    const switchToInfluencer = (influencer: Influencer) => {
        if (!isFoodfluenceEmail(user?.email || null)) {
            console.warn('Account switching is only available for foodfluence.ai/io emails');
            return;
        }
        setSwitchedInfluencerId(influencer.id);
        setIsSwitchedAccount(true);
        localStorage.setItem(SWITCHED_INFLUENCER_ID_KEY, influencer.id);
    };

    const resetToOwnAccount = () => {
        setSwitchedInfluencerId(null);
        setIsSwitchedAccount(false);
        localStorage.removeItem(SWITCHED_INFLUENCER_ID_KEY);
    };

    return (
        <InfluencerDataContext.Provider value={{ 
            influencerData, 
            loading, 
            switchToInfluencer,
            resetToOwnAccount,
            isSwitchedAccount
        }}>
            {children}
        </InfluencerDataContext.Provider>
    );
};
